import { t } from '@lingui/core/macro';
import type { StudentEmployerPlacementStatus } from '@duke-hq/svc-api-db';
import { StatusPill } from './StatusPill';

type PlacementStatusMetadata = {
  label: string;
  colorScheme: string;
};

// -- Status colour mapping ----------------------------------------------------
//
// The placement statuses collapse into two semantic colour groups so the
// drawer reads at a glance rather than as a rainbow:
//   - motion   (brand):  the placement is still running
//   - terminal (green / red / neutral): finished states, which carry no
//     attention colour of their own
function getPlacementStatusMetadata(): Record<
  StudentEmployerPlacementStatus,
  PlacementStatusMetadata
> {
  return {
    IN_PROGRESS: { label: t`In progress`, colorScheme: 'brand' },
    AWAITING_EVIDENCE: { label: t`Awaiting evidence`, colorScheme: 'brand' },
    COMPLETED: { label: t`Completed`, colorScheme: 'green' },
    FAILED: { label: t`Failed`, colorScheme: 'red' },
    CANCELLED: { label: t`Cancelled`, colorScheme: 'gray' },
  };
}

export function PlacementStatusBadge({
  status,
  onHold = false,
  'data-testid': dataTestId,
}: {
  status: StudentEmployerPlacementStatus;
  onHold?: boolean;
  'data-testid'?: string;
}): React.ReactElement {
  // On hold takes precedence over the underlying placement status.
  const { label, colorScheme } = onHold
    ? { label: t`On hold`, colorScheme: 'gray' }
    : getPlacementStatusMetadata()[status];
  return <StatusPill label={label} colorScheme={colorScheme} data-testid={dataTestId} />;
}
